import { useState } from "react";
import { useAtomValue } from "jotai";
import { ErrorState, Spinner } from "@common/index";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { useMountRead } from "@/hooks/useMountRead";
import { locationsAtom } from "@/store/locationAtoms";
import { LocationStatus } from "@models/location";
import { LocationResubmitCard } from "./LocationResubmitCard";

interface RejectedLocationsProps {
    // The flat user-level reason (E5-S6). Shown only when the locations call
    // yields no rejected locations — including a failed fetch — so the wall
    // never goes blank.
    fallbackReason?: string;
}

// The rejected branch of the pending wall (#198 / E5-S11): each rejected
// location with its reason and an in-place resubmit. Owns the API hook so the
// wall's other branches never mount it.
export const RejectedLocations = ({
    fallbackReason,
}: RejectedLocationsProps) => {
    const { getLocations } = useLocationApi();
    const locations = useAtomValue(locationsAtom);

    // Locations resubmitted during this visit — the refetch flips them to
    // Pending, but their card must stay to show the confirmation.
    const [resubmittedIds, setResubmittedIds] = useState<string[]>([]);
    // The status of the read THIS mount issued (#407), not
    // locationsFetchErrorAtom or the shared fetching flag: those follow every
    // surface's GET /locations and could hold a stale failure on this
    // component's first paint. Before its own read answers, the wall knows
    // nothing, so it shows the spinner rather than the fallback copy.
    // readStatus is this page's and only its latest read (a resubmit's
    // re-read) settles it. The list itself is the shared locationsAtom:
    // which answer lands there is decided by getLocations' request-id check,
    // not here.
    const { readStatus, reload: fetchLocations } = useMountRead(getLocations);

    const handleResubmitted = (locationId: string) => {
        setResubmittedIds((ids) => [...ids, locationId]);
        void fetchLocations();
    };

    const visibleLocations = locations.filter(
        (location) =>
            location.status === LocationStatus.Rejected ||
            resubmittedIds.includes(location.id),
    );

    if (readStatus === "loading" && visibleLocations.length === 0) {
        return (
            <div className="flex justify-center py-10">
                <Spinner size="lg" />
            </div>
        );
    }

    if (visibleLocations.length === 0) {
        // Defensive fallback (pre-#198 wall copy): the user is Rejected but the
        // list carries no rejected location — show the flat reason rather than
        // a blank wall. A FAILED read is not the same as "none rejected": the
        // resubmit cards are unreachable, so it also says so and offers Retry.
        return (
            <>
                <p className="text-base mt-6">
                    {fallbackReason ?? "Your application was not approved."}
                </p>
                {readStatus === "failed" && (
                    <ErrorState
                        message="Couldn't load your locations."
                        onRetry={fetchLocations}
                    />
                )}
            </>
        );
    }

    return (
        <ul className="mt-6 space-y-6">
            {visibleLocations.map((location) => (
                <LocationResubmitCard
                    key={location.id}
                    location={location}
                    isResubmitted={resubmittedIds.includes(location.id)}
                    onResubmitted={handleResubmitted}
                />
            ))}
        </ul>
    );
};
